import { ColDef } from 'ag-grid-community';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { FormInstancesByProject, FormQuestionType } from 'api/form';
import { Grid } from 'components/atoms/grid';
import { PageLoading } from 'components/atoms/loading';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import styles from 'components/molecules/forms/form-fields/rte.module.css';
import { flattenQuestions } from 'components/organisms/templates/forms/utils';
import { PermissionRuleName } from 'constant';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useTranslate } from 'hooks/use-translation';
import {
  useQueryAllAnswersByFormInstanceKey,
  useQueryFormInstanceById,
} from 'queries/form-instance';
import {
  assigneeRenderer,
  CreatedByRenderer,
  CreatedOnRenderer,
  dueDateRenderer,
  FormCellRenderer,
  MoreOptionsRenderer,
  UpdatedByRenderer,
  UpdatedOnRenderer,
} from './cell-renderer';
import { NoResponsesEmptyState } from './empty-states';

type FormResponseTablePropTypes = {
  projectFormInstance?: FormInstancesByProject;
  instanceId: number;
};

const Table = styled(Grid)`
  margin: 0 var(--spacing-04);
  &&& .ag-row {
    border-bottom: 1px solid var(--gray-20);
  }

  &&& .ag-overlay-no-rows-wrapper {
    display: block;
  }
  .ag-pinned-right-header,
  .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left):not(.ag-cell-range-single-cell) {
    border-left: 0px;
  }
  .ag-body-horizontal-scroll {
    display: inherit;
  }
  .ag-react-container > :not(:first-child) {
    display: none !important;
  }
`;

export const headerRenderer = (params) => {
  return (
    <RichTextEditor
      data={params.displayName}
      readonly
      className={styles['form-responses-header']}
    />
  );
};

export const FormResponseTable = ({
  instanceId,
}: FormResponseTablePropTypes) => {
  const { data: formInstaceDetails, status: formInstaceApiStatus } =
    useQueryFormInstanceById(instanceId);
  const { data: formInstanceAnswers, status: formAnswersApiStatus } =
    useQueryAllAnswersByFormInstanceKey(instanceId);
  const { isAllowedForCurrentUser } = usePermissionForRule();

  const translate = useTranslate();
  /**
   * Transformations below to convert these into the formates supported by Ag-grid
   * Please refer the types below to understand more
   */
  const columns = useMemo(() => {
    if (formInstaceDetails) {
      const moreActions: ColDef[] = [
        {
          headerName: translate('created_by'),
          cellRenderer: 'CreatedByRenderer',
          menuTabs: [],
        },
        {
          headerName: translate('created_on'),
          cellRenderer: 'CreatedOnRenderer',
          menuTabs: [],
        },
        {
          headerName: translate('updated_by'),
          cellRenderer: 'UpdatedByRenderer',
          menuTabs: [],
        },
        {
          headerName: translate('updated_on'),
          cellRenderer: 'UpdatedOnRenderer',
          menuTabs: [],
        },
        {
          field: 'more-actions',
          headerName: '',
          cellRenderer: 'MoreOptionsRenderer',
          maxWidth: 50,
          menuTabs: [],
          pinned: 'right',
          hide:
            !isAllowedForCurrentUser(
              PermissionRuleName.FormResponseEditAction,
            ) &&
            !isAllowedForCurrentUser(
              PermissionRuleName.FormResponseDeleteAction,
            ),
        },
      ];

      const {
        formTemplate: { formQuestions = [] },
      } = formInstaceDetails;

      const flattenedQuestions = flattenQuestions(formQuestions).filter(
        (question) =>
          question.formQuestionType !== FormQuestionType.MultipleChoiceGrid &&
          question.formQuestionType !== FormQuestionType.CheckBoxGrid,
      );

      return [
        {
          headerName: translate('due_date'),
          cellRenderer: 'dueDateRenderer',
          menuTabs: [],
        },
        {
          headerName: translate('assignees'),
          cellRenderer: 'assigneeRenderer',
          menuTabs: [],
        },
        ...flattenedQuestions
          .filter(
            (question) =>
              question.formQuestionType !== FormQuestionType.TextStatement,
          )
          .map(
            ({
              questionId,
              questionText,
              formQuestionType,
              questionMeta = [],
              options = [],
            }) =>
              ({
                field: questionId.toString(),
                headerName: questionText,
                headerComponent: 'headerRenderer',
                cellRenderer: 'responseCellRenderer',
                cellRendererParams: {
                  questionType: formQuestionType,
                  questionOptions: options,
                  questionMeta,
                },
                menuTabs: [],
              } as ColDef),
          ),
        ...moreActions,
      ];
    }

    return [];
  }, [formInstaceDetails, isAllowedForCurrentUser, translate]);

  const data = useMemo(() => {
    if (formInstanceAnswers?.answers) {
      const { taskId, projectId, dueDate, completedAt, assignee } =
        formInstanceAnswers;

      return formInstanceAnswers.answers.map((eachAnswerInstance) => {
        const {
          answerMetaJson = {},
          createdAt,
          createdBy,
          updatedAt,
          updatedBy,
          answerId,
          formInstanceId,
        } = eachAnswerInstance;

        return Object.values(answerMetaJson).reduce(
          (acc, { questionId, answers }) => {
            if (
              typeof answers === 'object' &&
              answers !== null &&
              !Array.isArray(answers)
            ) {
              return { ...acc, ...(answers as { [key: number]: number[] }) };
            }

            return { ...acc, [questionId]: answers };
          },
          {
            createdAt,
            createdBy,
            updatedAt,
            updatedBy,
            taskId,
            projectId,
            answerId,
            formInstanceId,
            dueDate,
            completedAt,
            assignee,
          },
        );
      });
    }

    return undefined;
  }, [formInstanceAnswers]);

  const apiStatus: 'idle' | 'error' | 'loading' | 'success' = useMemo(() => {
    if (
      formInstaceApiStatus === 'loading' ||
      formAnswersApiStatus === 'loading'
    ) {
      return 'loading';
    }

    if (
      formInstaceApiStatus === 'success' &&
      formAnswersApiStatus === 'success'
    ) {
      return 'success';
    }

    if (formInstaceApiStatus === 'error' || formAnswersApiStatus === 'error') {
      return 'error';
    }

    return 'idle';
  }, [formInstaceApiStatus, formAnswersApiStatus]);

  switch (apiStatus) {
    case 'loading': {
      return <PageLoading />;
    }
    case 'success': {
      if (columns.length) {
        return (
          <>
            <Table
              className="ag-theme-balham"
              frameworkComponents={{
                responseCellRenderer: FormCellRenderer,
                headerRenderer,
                emptyState: NoResponsesEmptyState,
                CreatedByRenderer,
                UpdatedByRenderer,
                MoreOptionsRenderer,
                CreatedOnRenderer,
                UpdatedOnRenderer,
                dueDateRenderer,
                assigneeRenderer,
              }}
              defaultColDef={{
                editable: false,
                lockPinned: true,
                flex: 1,
                minWidth: 150,
                resizable: true,
              }}
              rowData={data}
              columnDefs={columns}
              suppressContextMenu
              suppressMovableColumns
              noRowsOverlayComponentFramework={NoResponsesEmptyState}
            />
          </>
        );
      }

      return <></>;
    }

    case 'idle':
    case 'error':
    default: {
      return <></>;
    }
  }
};
